import {
    Input, Output, Conversion, ALL_FORMATS, BlobSource, BufferTarget,
    // Input format singletons/classes:
    Mp4InputFormat, QuickTimeInputFormat, WebMInputFormat, MatroskaInputFormat, CanvasSink,
    Mp3InputFormat, WaveInputFormat, OggInputFormat, FlacInputFormat,
    // Output formats we'll use:
    Mp4OutputFormat, WebMOutputFormat, MkvOutputFormat,
} from 'mediabunny';

/**
 * Fallback thumbnail extraction using native video element.
 * Works for any format the browser can play (including WebM VP9 from MediaRecorder).
 */
async function extractThumbnailWithVideoElement(
    file: Blob,
    targetTime: number,
    thumbW: number,
    thumbH: number
): Promise<Blob | null> {
    return new Promise((resolve) => {
        const video = document.createElement('video');
        video.muted = true;
        video.playsInline = true;
        video.preload = 'metadata';

        const objectUrl = URL.createObjectURL(file);
        let resolved = false;

        const cleanup = () => {
            if (!resolved) {
                resolved = true;
                URL.revokeObjectURL(objectUrl);
                video.src = '';
            }
        };

        const timeout = setTimeout(() => {
            console.warn('[extractThumbnailWithVideoElement] Timeout');
            cleanup();
            resolve(null);
        }, 10000); // 10 second timeout

        video.onerror = () => {
            clearTimeout(timeout);
            console.warn('[extractThumbnailWithVideoElement] Video element error');
            cleanup();
            resolve(null);
        };

        video.onloadedmetadata = () => {
            const seekTime = Math.min(Math.max(0, targetTime), video.duration || 0);
            video.currentTime = seekTime;
        };

        video.onseeked = () => {
            clearTimeout(timeout);
            try {
                const canvas = document.createElement('canvas');
                canvas.width = thumbW;
                canvas.height = thumbH;
                const ctx = canvas.getContext('2d');

                if (!ctx) {
                    cleanup();
                    resolve(null);
                    return;
                }

                ctx.drawImage(video, 0, 0, thumbW, thumbH);

                canvas.toBlob(
                    (blob) => {
                        cleanup();
                        resolve(blob);
                    },
                    'image/jpeg',
                    0.85
                );
            } catch (e) {
                console.warn('[extractThumbnailWithVideoElement] Error drawing frame:', e);
                cleanup();
                resolve(null);
            }
        };

        video.src = objectUrl;
    });
}

export type MediaProbe = {
    isVideo: boolean;                 // true if input has any video stream
    container: string;                // 'mp4'|'mov'|'webm'|'mkv'|'mp3'|'m4a'|'wav'|'flac'|'ogg'|...
    sizeBytes: number;                // file.size
    durationSec?: number;             // optional, if you have it
};

export const DEFAULT_LIMITS = {
    compressedAudioMaxMB: 100,   // MP3/M4A/WebM/Opus <= 100MB → use original
    veryLargeAudioMinMB: 200,    // >200MB → force extraction
};

const kTooSmallToBotherExtractMB = 5; // Under this size, extraction overhead may outweigh upload savings, so we skip it regardless of container

const COMPRESSED_AUDIO_CONTAINERS = new Set(['mp3', 'm4a', 'aac', 'webm', 'ogg', 'opus']);
const UNCOMPRESSED_AUDIO_CONTAINERS = new Set(['wav', 'aiff', 'pcm', 'flac']);

export function shouldExtractOpusAudioRendition(
    probe: MediaProbe,
    limits: typeof DEFAULT_LIMITS = DEFAULT_LIMITS
): boolean {
    const sizeMB = probe.sizeBytes / (1024 * 1024);
    const ext = probe.container.toLowerCase();

    // If the file has video → always extract Opus for upload speed
    if (probe.isVideo) return true;

    if (sizeMB < kTooSmallToBotherExtractMB) return false;

    // Uncompressed audio → always compress (upload speed)
    if (UNCOMPRESSED_AUDIO_CONTAINERS.has(ext)) return true;

    // Very large audio → extract Opus to reduce upload time
    if (sizeMB > limits.veryLargeAudioMinMB) return true;

    // Already-compressed + reasonably small → no need to extract
    if (COMPRESSED_AUDIO_CONTAINERS.has(ext) && sizeMB <= limits.compressedAudioMaxMB) {
        return false;
    }

    // Default: extract (safe path)
    return true;
}

export function shouldExtractEnhancementAudioRendition(
    probe: MediaProbe): boolean {
    // For now we only prepare client-side enhancement renditions for video uploads.
    // Audio uploads (including WAV) can be enhanced from the original on the backend.
    return probe.isVideo;
}

type ExtractResult = {
    blob: Blob;          // final audio-only file
    arrayBuffer: ArrayBuffer; // same bytes if you need them
    mime: string;        // suggested MIME
    ext: string;         // suggested extension
};

export async function extractOpusAudioRendition(file: Blob): Promise<ExtractResult> {
    const input = new Input({
        formats: ALL_FORMATS,
        source: new BlobSource(file),
    });

    const outputFormat = new WebMOutputFormat();   // WebM container with Opus codec
    const mime = 'audio/webm';
    const ext = 'webm';

    const target = new BufferTarget();
    const output = new Output({ format: outputFormat, target });

    const conversion = await Conversion.init({
        input,
        output,
        video: { discard: true },
        audio: {
            codec: 'opus',
            bitrate: 64000,
            sampleRate: 48000,
            numberOfChannels: 1,
        },
    });

    if (!conversion.isValid) {
        throw new Error(`Invalid client-side Opus extraction`);
    }

    await conversion.execute();

    const arrayBuffer = target.buffer;
    const blob = new Blob([arrayBuffer], { type: mime });
    return { blob, arrayBuffer, mime, ext };
}

export async function extractEnhancementAudioRendition(file: Blob): Promise<ExtractResult> {
    const input = new Input({
        formats: ALL_FORMATS,
        source: new BlobSource(file),
    });

    const format = await input.getFormat();
    const isWebmFamilyInput = format instanceof WebMInputFormat || format instanceof MatroskaInputFormat;

    // Prefer a container compatible with the source family to maximize direct packet copy.
    // Fall back to Matroska when a stricter container cannot accept the source codec.
    const candidates: Array<{ format: any; mime: string; ext: string }> = isWebmFamilyInput
        ? [
            { format: new WebMOutputFormat(), mime: 'audio/webm', ext: 'webm' },
            { format: new MkvOutputFormat(), mime: 'audio/x-matroska', ext: 'mka' },
            { format: new Mp4OutputFormat(), mime: 'audio/mp4', ext: 'm4a' },
        ]
        : [
            { format: new Mp4OutputFormat(), mime: 'audio/mp4', ext: 'm4a' },
            { format: new MkvOutputFormat(), mime: 'audio/x-matroska', ext: 'mka' },
            { format: new WebMOutputFormat(), mime: 'audio/webm', ext: 'webm' },
        ];

    let lastError: unknown = null;

    for (const candidate of candidates) {
        try {
            const target = new BufferTarget();
            const output = new Output({ format: candidate.format, target });

            const conversion = await Conversion.init({
                input,
                output,
                video: { discard: true },
                audio: {
                    // Keep source encoding when container supports it.
                    forceTranscode: false,
                },
            });

            if (!conversion.isValid) {
                continue;
            }

            await conversion.execute();

            const arrayBuffer = target.buffer;
            const blob = new Blob([arrayBuffer], { type: candidate.mime });
            return { blob, arrayBuffer, mime: candidate.mime, ext: candidate.ext };
        } catch (error) {
            lastError = error;
        }
    }

    throw new Error(`Invalid client-side enhancement extraction${lastError ? `: ${String(lastError)}` : ''}`);
}

export const ExtractMediaFileMetadata = async ({file, fileType}) => {
    // Extract resolution, duration, sampleRate, videoBitrate, audioBitrate, hasAudioStream, filesize and (for video) a mid-frame thumbnail
    // Returns: { width?, height?, duration, sampleRate?, videoBitrate?, audioBitrate?, hasAudioStream, fileSize, thumbnail?, thumbnailMimeType? }
    const fileSize = file?.size ?? null;

    // Prepare mediabunny input
    const source = new BlobSource(file);
    const input = new Input({ source, formats: ALL_FORMATS });

    const duration = await input.computeDuration(); // seconds

    const fmt = await input.getFormat();
    const container =
        fmt instanceof Mp4InputFormat      ? 'mp4'  :
            fmt instanceof QuickTimeInputFormat? 'mov'  :
                fmt instanceof WebMInputFormat     ? 'webm' :
                    fmt instanceof MatroskaInputFormat ? 'mkv'  :
                        fmt instanceof Mp3InputFormat      ? 'mp3'  :
                            fmt instanceof WaveInputFormat     ? 'wav'  :
                                fmt instanceof OggInputFormat      ? 'ogg'  :
                                    fmt instanceof FlacInputFormat     ? 'flac' : (file.type.split('/')[1] || 'unknown');


    let width: number | undefined = undefined;
    let height: number | undefined = undefined;
    let sampleRate: number | undefined = undefined;
    let videoBitrate: number | undefined = undefined;
    let audioBitrate: number | undefined = undefined;
    let hasAudioStream = false;
    let hasVideoStream = false;
    let thumbnail: Blob | undefined = undefined;
    let thumbnailMimeType: string | undefined = undefined;

    if (fileType === 'video') {
        const videoTrack = await input.getPrimaryVideoTrack();
        if (videoTrack) {
            hasVideoStream = true;
            width = (videoTrack as any).displayWidth ?? videoTrack.codedWidth;
            height = (videoTrack as any).displayHeight ?? videoTrack.codedHeight;

            // Bitrate via packet stats
            const stats = await videoTrack.computePacketStats();
            videoBitrate = stats?.averageBitrate;

            // Try to also get audio sample rate if present
            try {
                const audioTrack = await input.getPrimaryAudioTrack();
                if (audioTrack) {
                    hasAudioStream = true;
                    sampleRate = audioTrack.sampleRate;
                    try {
                        const aStats = await audioTrack.computePacketStats();
                        audioBitrate = aStats?.averageBitrate;
                    } catch (_) { /* ignore */ }
                }
            } catch (_) { /* ignore */ }

            // Mid-frame thumbnail
            const firstTs = await videoTrack.getFirstTimestamp();
            const lastTs = await videoTrack.computeDuration();
            const mid = firstTs + (lastTs - firstTs) / 2;

            // Bounded thumbnail size preserving aspect ratio (similar to server: 533x300)
            const displayW = (videoTrack as any).displayWidth ?? videoTrack.codedWidth;
            const displayH = (videoTrack as any).displayHeight ?? videoTrack.codedHeight;
            const maxWidth = 533;
            const maxHeight = 300;
            const aspectRatio = displayH > 0 ? displayW / displayH : 1;
            let thumbW: number;
            let thumbH: number;
            if (aspectRatio >= 1) {
                // Landscape or square
                thumbW = maxWidth;
                thumbH = Math.floor(maxWidth / (aspectRatio || 1));
                if (thumbH > maxHeight) {
                    thumbH = maxHeight;
                    thumbW = Math.floor(maxHeight * aspectRatio);
                }
            } else {
                // Portrait
                thumbH = maxHeight;
                thumbW = Math.floor(maxHeight * aspectRatio);
                if (thumbW > maxWidth) {
                    thumbW = maxWidth;
                    thumbH = Math.floor(maxWidth / (aspectRatio || 1));
                }
            }
            thumbW = Math.max(1, thumbW);
            thumbH = Math.max(1, thumbH);

            // Try mediabunny CanvasSink first, fall back to native video element if it fails
            try {
                const sink = new CanvasSink(videoTrack, {
                    width: Math.floor(thumbW * (window.devicePixelRatio || 1)),
                    height: Math.floor(thumbH * (window.devicePixelRatio || 1)),
                    fit: 'fill',
                });

                const iterator = sink.canvasesAtTimestamps([mid]);
                // Get first yielded canvas
                // @ts-ignore - for-await supported in TS targeting ES2018+
                for await (const wrapped of iterator) {
                    if (wrapped && wrapped.canvas) {
                        const cvs = wrapped.canvas as HTMLCanvasElement;
                        const blob: Blob | null = await new Promise(resolve =>
                            cvs.toBlob((b) => resolve(b), 'image/jpeg', 0.85)
                        );
                        if (blob) {
                            thumbnail = blob;
                            thumbnailMimeType = 'image/jpeg';
                        }
                    }
                    break;
                }
            } catch (canvasSinkError) {
                console.warn('[ExtractMediaFileMetadata] CanvasSink failed, trying native video element:', canvasSinkError);
            }

            // Fallback: use native video element if CanvasSink failed
            if (!thumbnail) {
                try {
                    const nativeResult = await extractThumbnailWithVideoElement(file, mid, thumbW, thumbH);
                    if (nativeResult) {
                        thumbnail = nativeResult;
                        thumbnailMimeType = 'image/jpeg';
                    }
                } catch (nativeError) {
                    console.warn('[ExtractMediaFileMetadata] Native thumbnail extraction also failed:', nativeError);
                }
            }
        }
    } else if (fileType === 'audio') {
        const audioTrack = await input.getPrimaryAudioTrack();
        if (audioTrack) {
            hasAudioStream = true;
            sampleRate = audioTrack.sampleRate;
            // compute overall audio bitrate from audio packets
            try {
                const stats = await audioTrack.computePacketStats();
                audioBitrate = stats?.averageBitrate;
            } catch (_) { /* ignore */ }
        }
    }

    return {
        width,
        height,
        duration,
        sampleRate,
        videoBitrate,
        audioBitrate,
        hasAudioStream,
        hasVideoStream,
        mimeType: file.type,
        container,
        fileSize,
        thumbnail,
        thumbnailMimeType,
    };
}

export const ExtractImageFileMetadata = async ({file}) => {
    // Extract resolution and thumbnail for image files
    // Returns: { width, height, fileSize, thumbnail?: Blob, thumbnailMimeType?: string }
    const fileSize = file?.size ?? null;

    // Use createImageBitmap when available for faster decode
    let bitmap: ImageBitmap | undefined;
    try {
        if (typeof createImageBitmap === 'function') {
            bitmap = await createImageBitmap(file);
        }
    } catch (_) { /* fallback to Image element below */ }

    let width: number;
    let height: number;
    if (bitmap) {
        width = bitmap.width;
        height = bitmap.height;
    } else {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = reject;
            el.src = URL.createObjectURL(file);
        });
        width = img.width;
        height = img.height;
    }

    // Create a small thumbnail (longest edge 320px), JPEG for compactness
    const maxThumb = 320;
    const scale = Math.min(1, Math.max(width, height) > 0 ? maxThumb / Math.max(width, height) : 1);
    const thumbW = Math.max(1, Math.round(width * scale));
    const thumbH = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = thumbW;
    canvas.height = thumbH;
    const ctx = canvas.getContext('2d');
    if (ctx) {
        if (bitmap) {
            ctx.drawImage(bitmap, 0, 0, thumbW, thumbH);
            bitmap.close?.();
        } else {
            const imgEl = await new Promise<HTMLImageElement>((resolve, reject) => {
                const el = new Image();
                el.onload = () => resolve(el);
                el.onerror = reject;
                el.src = URL.createObjectURL(file);
            });
            ctx.drawImage(imgEl, 0, 0, thumbW, thumbH);
        }
    }

    const thumbnail: Blob | null = await new Promise(resolve =>
        canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.85)
    );

    return {
        width,
        height,
        fileSize,
        mimetype: file.type,
        thumbnail: thumbnail ?? undefined,
        thumbnailMimeType: thumbnail ? 'image/jpeg' : undefined,
    };
}
